import { AxiosError } from "axios";
import { appendErrors, FieldError } from "react-hook-form";

import { ErrorResponse, ValidationIssues } from "@reflag/shared/api";

export function convertApiIssuesToHookFormErrors(
  issues: ValidationIssues,
  options: {
    criteriaMode: "firstError" | "all";
  } = { criteriaMode: "firstError" },
): Record<string, FieldError> {
  const flattened = Object.entries(issues).flatMap(([path, errors]) => {
    return errors.map((error) => ({
      path: !path ? "root" : path,
      message: error,
    }));
  });

  const validateAllFieldCriteria = options.criteriaMode === "all";

  const errors: Record<string, FieldError> = {};
  for (const { path, message } of flattened) {
    errors[path] ??= { message, type: "validate" };

    if (validateAllFieldCriteria) {
      const res = errors[path].types?.["validate"];

      errors[path] = appendErrors(
        path,
        validateAllFieldCriteria,
        errors,
        "validate",
        typeof res === "string"
          ? [res, message]
          : typeof res === "object"
            ? [...res, message]
            : message,
      ) as FieldError;
    }
  }

  return errors;
}

export function translateError(
  error: AxiosError<ErrorResponse> | Error | any,
  title?: string | ((error: AxiosError<ErrorResponse>) => string),
) {
  const errorResponse =
    "name" in error && error.name === "AxiosError" && error.response
      ? error.response.data
        ? { ...error.response.data, status: error.response.status }
        : {
            status: error.response.status || 500,
            error: {
              message: "Unknown error",
              code: "unknown_error",
            },
          }
      : error instanceof Error
        ? {
            status: 500,
            error: {
              message: error.message,
              code: "unknown_error",
            },
          }
        : {
            status: 500,
            error: {
              message: error?.message || "Unknown error",
              code: "unknown_error",
            },
          };

  const individualErrors = errorResponse.issues
    ? convertApiIssuesToHookFormErrors(errorResponse.issues)
    : [];

  let prefix = "Error";
  switch (errorResponse.status) {
    case 401:
      prefix = "Unauthorized";
      break;
    case 403:
      prefix = "Forbidden";
      break;
    case 404:
      prefix = "Not found";
      break;
    case 409:
      prefix = "Conflict detected";
      break;
    case 400:
      prefix = "Bad input data";
      break;
    case 500:
      prefix = "Server error";
      break;
    default:
      break;
  }

  const description = `${prefix}: ${
    errorResponse.error?.message ||
    error.message ||
    "An unexpected error has occurred"
  }`;

  const actualTitle =
    (typeof title === "function" ? title(error) : title) ||
    "There was a problem with your request";

  return {
    title: actualTitle,
    description,
    fieldErrors: individualErrors,
    status: errorResponse.status,
  };
}
